import * as Feather from "react-feather";
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Slider from "react-slick";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { nanoid } from "nanoid";
import { api, baseURL, customerReqConfig } from "../../src/include/api";
import { getCustomer, ratingAvg1 } from "../include/function";
import { useNotification } from "../Toast/ToastProvider";
import { useCart } from "../../src/components/context/UserCartCountContext";
import {
  addToCartHandler,
  handleWishlist,
  loadCartData,
  loadWishlist,
} from "../helper/cartUtils";

// Carousel Item
const CarouselItem = ({ slide }) => {
  const handleClick = () => {
    if (slide.linkUrl) {
      window.open(slide.linkUrl, "_blank", "noopener,noreferrer");
    }
  };
  return (
    <div
      className="mx-2"
      style={{ height: "100%", flex: "1 1 0", cursor: "pointer" }}
      onClick={handleClick}
    >
      <img
        className="fit-cover w-100 h-100"
        src={slide.imageUrl ? `${baseURL}/${slide.imageUrl}` : ""}
        alt=""
      />
    </div>
  );
};

// Main Component
const TopSellingOne = ({ bestSellers, bestSellerCategoryId }) => {
  const sliderRef = useRef(null);
  const [addedToCartMap, setAddedToCartMap] = useState({});
  const { fetchCartCount, setTotalWishlistItems } = useCart();
  const [loading, setLoading] = useState(null);
  const navigate = useNavigate();
  const toast = useNotification();
  const customer = getCustomer();
  const [wishlistProductIds, setWishlistProductIds] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [slideList, setSlideList] = useState([]);
  const [savedForLaterData, setSavedForLaterData] = useState({ products: [] });

  useEffect(() => {
    if (customer?.id) {
      loadCartData(customer.id, () => {}, setAddedToCartMap);
    }
  }, [customer?.id]);

  useEffect(() => {
    if (customer?.id) {
      loadWishlist(
        customer.id,
        setWishlistProductIds,
        setTotalWishlistItems,
        setFetchLoading,
      );
    }
  }, [customer?.id, setTotalWishlistItems]);

  useEffect(() => {
    const handleCartUpdate = () => {
      if (customer?.id) {
        loadCartData(customer.id, () => {}, setAddedToCartMap);
      }
    };

    window.addEventListener("cartUpdated", handleCartUpdate);
    return () => window.removeEventListener("cartUpdated", handleCartUpdate);
  }, [customer?.id, setAddedToCartMap]);

  useEffect(() => {
    const handleWishlistUpdate = () => {
      if (customer?.id) {
        loadWishlist(
          customer.id,
          setWishlistProductIds,
          setTotalWishlistItems,
          () => {},
        );
      }
    };

    window.addEventListener("wishlistUpdated", handleWishlistUpdate);
    return () =>
      window.removeEventListener("wishlistUpdated", handleWishlistUpdate);
  }, [customer?.id, setWishlistProductIds, setTotalWishlistItems]);

  // Add to cart handler
  const handleAddToCart = (product) => {
    addToCartHandler({
      product,
      customer,
      setAddedToCartMap,
      setSavedForLaterData,
      fetchCartCount,
      toast,
      navigate,
      setLoading,
      savedForLaterData,
    });
  };

  // Load Slides
  const getSlides = () => {
    setFetchLoading(true);
    api
      .get("/slide_show/", customerReqConfig())
      .then((response) => {
        if (response.status === 200) {
          setSlideList(response.data.data);
        }
      })
      .catch((error) => {
        console.error("Error fetching slides", error);
      })
      .finally(() => {
        setFetchLoading(false);
      });
  };

  useEffect(() => {
    getSlides();
  }, []);

  // Wishlist Handler
  const wishListHandler = async (item) => {
    // Prevent double-clicks for the same product
    if (loading === item.productId) return; // Already processing this item

    setLoading(item.productId);
    await handleWishlist({
      item,
      customer,
      wishlistProductIds,
      setWishlistProductIds,
      setTotalWishlistItems,
      toast,
    });
    setLoading(null);
  };

  // Slider Settings
  const settings = {
    dots: false,
    arrows: false, // disable default arrows
    infinite: true,
    speed: 1000,
    slidesToShow: 4,
    slidesToScroll: 1,
    initialSlide: 0,
    autoplay: true,
    responsive: [
      { breakpoint: 1399, settings: { slidesToShow: 3 } },
      { breakpoint: 1199, settings: { slidesToShow: 3 } },
      { breakpoint: 575, settings: { slidesToShow: 1 } },
    ],
  };

  const TopSellingProductsSlides = slideList?.filter(
    (slide) =>
      slide.status === "active" && slide.type === "top selling products",
  );
  // Helper function to calculate price based on product type
  const calculateProductPrice = (item) => {
    const isB2C = !customer || customer.customerType === "individual";
    const basePrice = parseFloat(item.price) || 0;

    // Flow 1: qtyAndPrice has values - Find MINIMUM minQty and show its price
    if (item.qtyAndPrice && item.qtyAndPrice.length > 0) {
      // Find the entry with the minimum minQty value
      const minQtyEntry = item.qtyAndPrice.reduce((minEntry, currentEntry) => {
        return currentEntry.minQty < minEntry.minQty ? currentEntry : minEntry;
      }, item.qtyAndPrice[0]);

      if (minQtyEntry) {
        const displayPrice = isB2C
          ? parseFloat(minQtyEntry.setB2cPrice)
          : parseFloat(minQtyEntry.setB2bPrice);

        // Get quantity range from the minimum entry
        const minQty = minQtyEntry.minQty || 0;
        const maxQty = minQtyEntry.maxQty || 0;
        const basedOn = minQtyEntry.basedOn || "qty";

        // Calculate discount percentage
        let discountInfo = null;
        if (basePrice > 0 && basePrice > displayPrice) {
          const discountAmount = basePrice - displayPrice;
          const discountPercent = ((discountAmount / basePrice) * 100).toFixed(
            2,
          );
          discountInfo = {
            value: discountPercent,
            mode: "%",
          };
        }

        return {
          displayPrice: isNaN(displayPrice)
            ? basePrice.toFixed(2)
            : displayPrice.toFixed(2),
          qtyRange: `${minQty} - ${maxQty} ${basedOn}`,
          originalPrice: basePrice.toFixed(2),
          discountInfo: discountInfo,
          priceType: "qtyAndPrice",
          basedOn: basedOn,
          minQtyValue: minQty,
        };
      }
    }

    // Flow 2: productVariant has values - Show min-max retail and selling prices
    if (item.productVariant && item.productVariant.length > 0) {
      const retailPrices = item.productVariant
        .map((variant) => {
          return parseFloat(variant.price) || 0;
        })
        .filter((price) => !isNaN(price) && price > 0);

      const sellingPrices = item.productVariant
        .map((variant) => {
          const variantPrice = parseFloat(variant.price) || 0;

          // Calculate discount for variant
          let finalPrice = variantPrice;
          const discMode = isB2C
            ? (variant.b2cDiscountMode || "").toLowerCase()
            : (variant.b2bDiscountMode || "").toLowerCase();
          const discValue = isB2C
            ? parseFloat(variant.b2cDiscountValue) || 0
            : parseFloat(variant.b2bDiscountValue) || 0;

          if (discMode === "percent") {
            finalPrice = variantPrice - (variantPrice * discValue) / 100;
          } else if (discMode === "amount") {
            finalPrice = variantPrice - discValue;
          }

          return Math.max(0, finalPrice);
        })
        .filter((price) => !isNaN(price) && price > 0);

      if (retailPrices.length > 0 && sellingPrices.length > 0) {
        const minRetail = Math.min(...retailPrices);
        const maxRetail = Math.max(...retailPrices);
        const minSelling = Math.min(...sellingPrices);
        const maxSelling = Math.max(...sellingPrices);

        // Create retail price display string
        let retailPriceDisplay = "";
        if (retailPrices.length > 1) {
          retailPriceDisplay = `₹ ${minRetail.toFixed(2)} - ${maxRetail.toFixed(2)}`;
        } else {
          retailPriceDisplay = `₹ ${minRetail.toFixed(2)}`;
        }

        // Create selling price display string
        let sellingPriceDisplay = "";
        if (sellingPrices.length > 1) {
          sellingPriceDisplay = `₹ ${minSelling.toFixed(2)} - ${maxSelling.toFixed(2)}`;
        } else {
          sellingPriceDisplay = `₹ ${minSelling.toFixed(2)}`;
        }

        return {
          retailPriceDisplay: retailPriceDisplay,
          sellingPriceDisplay: sellingPriceDisplay,
          priceType: "productVariant",
        };
      }
    }

    // Flow 3: Both arrays are empty - use current logic
    const b2bMode = (item.b2bDiscountMode || "").toLowerCase();
    const b2cMode = (item.b2cDiscountMode || "").toLowerCase();

    let b2bDiscountUnitPrice = basePrice;
    if (b2bMode === "percent") {
      const disc = parseFloat(item.b2bDiscountValue) || 0;
      b2bDiscountUnitPrice = basePrice - (basePrice * disc) / 100;
    } else if (b2bMode === "amount") {
      const disc = parseFloat(item.b2bDiscountValue) || 0;
      b2bDiscountUnitPrice = basePrice - disc;
    }

    let b2cDiscountUnitPrice = basePrice;
    if (b2cMode === "percent") {
      const disc = parseFloat(item.b2cDiscountValue) || 0;
      b2cDiscountUnitPrice = basePrice - (basePrice * disc) / 100;
    } else if (b2cMode === "amount") {
      const disc = parseFloat(item.b2cDiscountValue) || 0;
      b2cDiscountUnitPrice = basePrice - disc;
    }

    const finalPrice = isB2C ? b2cDiscountUnitPrice : b2bDiscountUnitPrice;
    const displayPrice = Math.max(0, Math.round(finalPrice)).toFixed(2);

    // Get discount info for display
    const custDiscVal = isB2C ? item.b2cDiscountValue : item.b2bDiscountValue;
    const custDiscMode = isB2C
      ? b2cMode === "percent"
        ? "%"
        : "₹"
      : b2bMode === "percent"
        ? "%"
        : "₹";

    return {
      displayPrice,
      priceRange: null,
      originalPrice: basePrice.toFixed(2),
      discountInfo:
        parseFloat(custDiscVal) > 0
          ? { value: custDiscVal, mode: custDiscMode }
          : null,
      priceType: "basePrice",
    };
  };
  return (
    <section className="top-selling pt-32" id="Top_Selling_Products">
      <div className="container container-lg">
        <div className="border border-gray-100 p-24 rounded-16">
          <div className="section-heading mb-24">
            <div className="flex-between flex-wrap gap-8">
              <h5 className="mb-0">Top Selling Products</h5>
              <div className="flex-align mr-point gap-16">
                <Link
                  to={`/category-banner-products/${bestSellerCategoryId}`}
                  className="text-sm fw-medium text-gray-700 hover-text-main-600 hover-text-decoration-underline"
                >
                  View All Deals
                </Link>
                <div className="flex-align gap-8">
                  <button
                    type="button"
                    onClick={() => sliderRef.current?.slickPrev()}
                    style={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "50%",
                      backgroundColor: "white",
                      border: "1px solid #cbd5e1", // border-gray-300
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      cursor: "pointer",
                      transition: "background-color 0.3s, color 0.3s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#FA6800";
                      e.currentTarget.style.color = "white";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "white";
                      e.currentTarget.style.color = "black";
                    }}
                    aria-label="Previous"
                  >
                    <i
                      className="ph ph-caret-left text-xl"
                      style={{ margin: 0, lineHeight: 1 }}
                    />
                  </button>

                  <button
                    type="button"
                    onClick={() => sliderRef.current?.slickNext()}
                    style={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "50%",
                      backgroundColor: "white",
                      border: "1px solid #cbd5e1",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      cursor: "pointer",
                      transition: "background-color 0.3s, color 0.3s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#FA6800";
                      e.currentTarget.style.color = "white";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "white";
                      e.currentTarget.style.color = "black";
                    }}
                    aria-label="Next"
                  >
                    <i
                      className="ph ph-caret-right text-xl"
                      style={{ margin: 0, lineHeight: 1 }}
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="row g-12">
            <div className="col-md-4">
              <div className="position-relative rounded-16 overflow-hidden p-28 z-1 text-center w-100 h-100">
                {fetchLoading ? (
                  <div className="w-100 bg-light" style={{ height: "300px" }} />
                ) : (
                  <Carousel
                    autoPlay
                    className="position-absolute inset-block-start-0 inset-inline-start-0 w-100 h-100 cover-img"
                    infiniteLoop
                    interval={3000}
                    preventMovementUntilSwipeScrollTolerance
                    showThumbs={false}
                    transitionTime={100}
                    showStatus={false}
                    showIndicators={false}
                  >
                    {TopSellingProductsSlides?.map((slide) => (
                      <CarouselItem slide={slide} key={nanoid()} />
                    ))}
                  </Carousel>
                )}
                <div className="d-md-block d-none mt-36">
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                </div>
              </div>
            </div>
            <div className="col-md-8 position-relative">
              <div className="top-selling-product-slider">
                <Slider ref={sliderRef} {...settings}>
                  {bestSellers.map((item, index) => {
                    const isAdded = !!addedToCartMap[item.productId];
                    const priceInfo = calculateProductPrice(item);

                    return (
                      <div
                        key={index}
                        className="product-card h-100 p-16 border border-gray-100 hover-border-main-600 rounded-16 position-relative transition-2"
                      >
                        <Link
                          to={`/product-details/${item.productId}`}
                          className="product-card__thumb flex-center rounded-8 bg-gray-50 position-relative"
                        >
                          {priceInfo.discountInfo && (
                            <span className="product-card__badge bg-main-600 px-8 py-4 text-sm text-white position-absolute inset-inline-start-0 inset-block-start-0">
                              {priceInfo.discountInfo.mode === "%"
                                ? `Offer ${priceInfo.discountInfo.value}%`
                                : `Offer ₹${priceInfo.discountInfo.value}`}
                            </span>
                          )}
                          <img
                            src={baseURL + "/" + item.mainImage}
                            alt=""
                            className=""
                            style={{ width: "100px !important" }}
                          />
                        </Link>
                        <div className="product-card__content mt-16">
                          <div className="d-flex justify-content-between align-items-center">
                            {item?.ratingAndReview?.length > 0 ? (
                              <div className="d-flex align-items-center gap-2">
                                <div
                                  className="badge bg-success d-inline-flex text-center"
                                  style={{ fontSize: "13px" }}
                                >
                                  {ratingAvg1(item.ratingAndReview)}
                                  <i className="ph-fill ph-star ms-1" />
                                </div>
                                <span className="fw-medium text-gray-500">
                                  ({item.ratingAndReview.length})
                                </span>
                              </div>
                            ) : (
                              <div />
                            )}
                            <div
                              className="d-flex align-items-center"
                              title={
                                wishlistProductIds.includes(
                                  String(item.productId),
                                )
                                  ? "Already in Wishlist"
                                  : "Add to Wishlist"
                              }
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                wishListHandler(item);
                              }}
                              style={{ cursor: "pointer" }}
                              disabled={loading === item.productId}
                            >
                              {loading === item.productId ? (
                                <div
                                  className="spinner-border spinner-border-sm text-secondary"
                                  role="status"
                                >
                                  <span className="visually-hidden">
                                    Loading...
                                  </span>
                                </div>
                              ) : wishlistProductIds.includes(
                                  String(item.productId),
                                ) ? (
                                <Feather.Heart
                                  fill="red"
                                  color="red"
                                  size={22}
                                  strokeWidth={1.6}
                                />
                              ) : (
                                <Feather.Heart size={22} strokeWidth={1.6} />
                              )}
                            </div>
                          </div>
                          <h6 className="title text-md fw-semibold mt-12 mb-8">
                            <Link
                              to={`/product-details/${item.productId}`}
                              className="link text-line-2"
                              style={{
                                display: "inline-block",
                                width: "90%",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              tabIndex={0}
                            >
                              {item.name}
                            </Link>
                          </h6>
                          <div className="flex-align gap-4">
                            <span className="text-tertiary-600 text-md d-flex">
                              <i className="ph-fill ph-storefront" />
                            </span>
                            <span className="text-gray-500 text-xs">
                              By PTC
                            </span>
                          </div>
                          <div className="mt-8">
                            <div
                              className="progress w-100 bg-color-three rounded-pill h-4"
                              role="progressbar"
                              aria-label="Basic example"
                              aria-valuenow={35}
                              aria-valuemin={0}
                              aria-valuemax={100}
                            >
                              <div
                                className="progress-bar bg-tertiary-600 rounded-pill"
                                style={{ width: "35%" }}
                              />
                            </div>
                            <span className="text-gray-900 text-xs fw-medium mt-8">
                              Sold: 18/35
                            </span>
                          </div>
                          <div className="product-card__price my-20">
                            {priceInfo.priceType === "productVariant" ? (
                              <>
                                {/* Variant products: Show retail price range with clean strikethrough */}
                                <div className="text-gray-400 text-md fw-semibold text-decoration-line-through mb-2">
                                  {priceInfo.retailPriceDisplay}
                                </div>
                                <div className="text-heading text-md fw-semibold">
                                  {priceInfo.sellingPriceDisplay}
                                </div>
                              </>
                            ) : (
                              <>
                                {/* Non-variant products: Show original price with clean strikethrough */}
                                {priceInfo.originalPrice && (
                                  <div className="text-gray-400 text-md fw-semibold text-decoration-line-through mb-2">
                                    ₹ {priceInfo.originalPrice}{" "}
                                    {/* Removed nested span causing style conflicts */}
                                  </div>
                                )}
                                <span className="text-heading text-md fw-semibold">
                                  ₹ {priceInfo.displayPrice}
                                  {priceInfo.qtyRange && (
                                    <span className="text-gray-500 fw-normal ms-2">
                                      ({priceInfo.qtyRange})
                                    </span>
                                  )}
                                  <span className="text-gray-500 fw-normal">
                                    {priceInfo.priceType === "qtyAndPrice"
                                      ? ""
                                      : "/Qty"}
                                  </span>
                                </span>
                              </>
                            )}
                          </div>
                          <button
                            className={`product-card__cart btn py-11 px-24 rounded-8 flex-center gap-8 fw-medium ${
                              isAdded
                                ? "bg-main-600 text-white"
                                : "bg-gray-50 text-heading hover-bg-main-600 hover-text-white"
                            }`}
                            onClick={() => {
                              if (isAdded) {
                                navigate("/cart");
                              } else {
                                handleAddToCart(item);
                              }
                            }}
                            disabled={loading === item.productId}
                          >
                            {loading === item.productId ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm"
                                  role="status"
                                  aria-hidden="true"
                                />
                                <span className="visually-hidden">
                                  Loading...
                                </span>{" "}
                                Adding...
                              </>
                            ) : isAdded ? (
                              "\u00A0\u00A0Go To Cart"
                            ) : (
                              "Add To Cart"
                            )}
                            <i className="ph ph-shopping-cart" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </Slider>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TopSellingOne;
